import { z } from 'zod';
import { BaseErrorSchema } from '../../shared/error.schema';
import { ProcessStatusSchema, ProcessWithStepsSchema } from '../../entities/process';
import { ProcessStepAssignmentSchema } from '../../entities/process-step-assignment';
import { CapacityBindingSchema, CapacitySourcesSchema } from '../../shared/capacity';
import { OpenDraftAssignmentSchema } from '../process-draft/process-draft.schema';
/**
 * Toggle flag for an export section.
 *
 * Flags arrive on the query string as strings (`?includeSteps=false`), so
 * `z.coerce.boolean()` is unusable — it runs `Boolean("false")`, which is
 * `true`. Parse the literal string forms explicitly and default each toggle
 * to on so an omitted flag means "include this section".
 */ const exportToggle = ()=>z.union([
        z.boolean(),
        z.enum([
            'true',
            'false'
        ])
    ]).transform((value)=>value === true || value === 'true').default(true);
/**
 * Export Process Query Schema
 * GET /api/v2/process/:id/export
 *
 * Each flag gates one section of the export. Referenced entities (owner,
 * department, assignees, helpers, …) are always emitted as fully populated
 * objects. `includeContext` embeds the full-org analyzer context and is off by
 * default because it is heavy. `format` only affects controller serialization
 * (the service ignores it).
 */ export const ExportProcessQuerySchema = z.object({
    includeSteps: exportToggle(),
    includeAssignments: exportToggle(),
    includeCapacity: exportToggle(),
    includeContext: exportToggle().default(false),
    format: z.enum([
        'json',
        'yaml'
    ]).default('json')
});
/**
 * Export Process Response Schema
 *
 * Describes the logical export document. It is not returned as a JSON API body
 * but serialized to a downloadable attachment whose bytes are this shape
 * encoded as JSON or YAML (per the `format` query flag). Every section except
 * the process core, `isCompleted` and `status` is toggle-gated, hence
 * `.optional()`. `isCompleted` stays for the external document format: it is
 * stage A alone, while `status` carries the full four-value verdict.
 * References are always fully populated.
 *
 * `context` carries the org-wide analyzer context when `includeContext` is on:
 * a record of `roles`/`departments`/`systems`/`teams`/`organigram`, each a
 * YAML-formatted string produced by `buildContextVariables`.
 */ export const ExportProcessResponseSchema = z.object({
    process: ProcessWithStepsSchema,
    isCompleted: z.boolean(),
    status: ProcessStatusSchema,
    pendingAssignments: z.array(ProcessStepAssignmentSchema).optional(),
    openAssignments: z.array(OpenDraftAssignmentSchema).optional(),
    capacityBinding: CapacityBindingSchema.nullable().optional(),
    stepCapacities: z.record(z.string(), CapacityBindingSchema).nullable().optional(),
    capacitySources: CapacitySourcesSchema.nullable().optional(),
    stepCapacitySources: z.record(z.string(), CapacitySourcesSchema).nullable().optional(),
    context: z.record(z.string(), z.string()).optional()
});
/**
 * Export Process Error Schema
 */ export const ExportProcessErrorSchema = BaseErrorSchema;

//# sourceMappingURL=export-process.schema.js.map